/* ---------------------------------------------------------------------------
   Photo drop slot.

   The design uses an <image-slot> element wherever a bridge photograph belongs.
   This is the real implementation: click to pick a file, or drag one on. Images
   are held as object URLs in a module-level map keyed by slot id, so a slot keeps
   its picture across re-renders without the state having to know about blobs.

   Local preview only — there is no backend endpoint this uploads to. A picked
   file lives for the session and is gone on reload.

   This is NOT the path a field photograph takes. A photograph attached to an
   inspection is downscaled, posted with the entry and stored in R2 with an
   author — see src/screens/inspectionDialog.ts and functions/lib/photos.ts.
   What is left here is the elevation slot on a record, a placeholder for a
   picture of the structure itself, which has no home yet because it has no date,
   no author and nothing to hang it off.

   Object URLs are revoked when a slot is overwritten or cleared, so repeated
   drops don't leak.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';

const images = new Map<string, string>();

/** Listeners notified when any slot changes, so screens can re-render. */
const listeners = new Set<() => void>();

export function onImageChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(): void {
  for (const fn of listeners) fn();
}

function setImage(id: string, url: string | null): void {
  const previous = images.get(id);
  if (previous) URL.revokeObjectURL(previous);
  if (url) {
    images.set(id, url);
  } else {
    images.delete(id);
  }
  notify();
}

/**
 * The image a slot is holding, if it is holding one.
 *
 * For surfaces that print what a slot has rather than offering the slot: the
 * inventory sheet draws plates, not drop targets, and has to be able to ask
 * whether there is anything to draw.
 */
export function slotImage(id: string): string | undefined {
  return images.get(id);
}

export function clearAllImages(): void {
  for (const url of images.values()) URL.revokeObjectURL(url);
  images.clear();
  notify();
}

function accept(id: string, file: File | null | undefined): void {
  if (!file || !file.type.startsWith('image/')) return;
  setImage(id, URL.createObjectURL(file));
}

/**
 * Render a photo slot. The slot fills its positioned parent, so the caller is
 * responsible for giving that parent a size (`position:relative` + a height or
 * aspect-ratio).
 */
export function imageSlot(
  id: string,
  placeholder: string,
  opts: { radius?: number; compact?: boolean } = {},
): HTMLElement {
  const url = images.get(id);

  const input = h('input', {
    type: 'file',
    accept: 'image/*',
    class: 'visually-hidden',
    onchange: (e: Event) => {
      const target = e.target as HTMLInputElement;
      accept(id, target.files?.[0]);
      target.value = '';
    },
  });

  const slot = h(
    'button',
    {
      type: 'button',
      class: 'imgslot',
      style: opts.radius ? { borderRadius: `${opts.radius}px` } : undefined,
      'aria-label': url ? `Replace photo: ${placeholder}` : placeholder,
      /* Reached through the event rather than closed over: render() patches the
         previous slot in place, which keeps the input that is already in the
         document and would leave this handler holding a detached one. */
      onclick: (e: Event) =>
        (e.currentTarget as HTMLElement).querySelector<HTMLInputElement>('input')?.click(),
      ondragover: (e: Event) => {
        e.preventDefault();
        (e.currentTarget as HTMLElement).classList.add('imgslot--over');
      },
      ondragleave: (e: Event) => {
        (e.currentTarget as HTMLElement).classList.remove('imgslot--over');
      },
      ondrop: (e: Event) => {
        e.preventDefault();
        const de = e as DragEvent;
        (e.currentTarget as HTMLElement).classList.remove('imgslot--over');
        accept(id, de.dataTransfer?.files?.[0]);
      },
    },
    url
      ? h('img', { src: url, alt: placeholder })
      : h(
          'span',
          { class: 'imgslot__ph' },
          icon(ICONS.image, { size: opts.compact ? 14 : 18, stroke: 'var(--txt3)' }),
          opts.compact ? null : placeholder,
        ),
    input,
  );

  if (!url) return slot;

  // Wrap so the clear button isn't nested inside the button element.
  return h(
    'span',
    { style: 'position:absolute;inset:0;display:block' },
    slot,
    h('button', {
      type: 'button',
      class: 'imgslot__clear',
      title: 'Remove photo',
      'aria-label': 'Remove photo',
      text: '✕',
      onclick: (e: Event) => {
        e.stopPropagation();
        setImage(id, null);
      },
    }),
  );
}
